/**
 * NoContactarPage — `/activa/no-contactar` (§4.quater del PRD).
 *
 * La "lista negra": personas que pidieron no ser contactadas.
 * El sistema la chequea antes de cada envío y la respeta siempre,
 * por encima de cualquier campaña. Requisito legal (Ley 25.326).
 *
 * Datos REALES del backend (`GET/POST /activa/do-not-contact`).
 *
 * Una baja que dejó el simulador del WhatsApp oficial (la BAJA de «Simular respuesta») viene con
 * `simulado: true`: con la cuenta real no frena ningún canal (ni el oficial, ni el WhatsApp de Activa,
 * ni el mail). La fila lo dice con texto visible, no con un tooltip: sin eso se veía igual que una
 * persona que pidió la baja de verdad, y quien la pedía por otra vía no se cargaba porque «ya estaba».
 *
 * Levantar una baja es del equipo de Deenex (decisión de Alan del 29/10/2026): volver a escribirle a alguien que pidió
 * que no lo decide Deenex. A una persona de un cliente no se le muestra «Levantar» (el backend le da 403 SOLO_EQUIPO
 * igual) y la lista le dice a quién pedírselo. Registrar una baja sí puede: sólo achica a quién se le escribe.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Ban, MessageCircle, Mail, Loader2, Plus, Undo2 } from "lucide-react";
import { activaApi } from "../api";
import { exigirOk } from "../utils/exigirOk";
import { plural } from "../utils/format";
import { useEsEquipoDeenex } from "../hooks/useEsEquipoDeenex";

type Fila = { id?: string; contacto: string; contactoNorm?: string; canal: "WhatsApp" | "Email"; motivo?: string; createdAt?: string; simulado?: boolean };

const TEXTO_SIMULADA = "Del modo simulado: con la cuenta real no frena ningún canal";

/** Lo que ve una persona de un cliente donde el equipo tiene «Levantar». */
const TEXTO_LEVANTAR_SOLO_EQUIPO = "Para volver a escribirle, pedíselo al equipo de Deenex.";

/**
 * Cómo se ve cada baja. Lo primero es el teléfono normalizado que guarda el backend (`contactoNorm`,
 * «+5491155443322»), que es además con lo que se bloquea. Antes se le anteponía un «+» a cualquier texto
 * de 8 a 15 dígitos, pero una baja cargada a mano guarda lo que se tipeó: «1155443322» se veía
 * «+1155443322», un número de Estados Unidos. Sin la forma normalizada (filas viejas), sólo se le pone
 * «+» a lo que ya trae el 54; un mail queda tal cual.
 */
function contactoParaMostrar(n: Pick<Fila, "contacto" | "contactoNorm">): string {
  const norm = String(n.contactoNorm ?? "").trim();
  if (norm.startsWith("+")) return norm;
  const v = String(n.contacto ?? "").trim();
  return /^54\d{8,13}$/.test(v) ? `+${v}` : v;
}

function fmtFecha(iso?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

/**
 * `embebida`: la usa «Bajas y consentimientos» del WhatsApp oficial (la lista es UNA por marca,
 * compartida por todos los canales). Ahí el layout del oficial ya pone el contenedor, el ancho y
 * su propio encabezado: repetirlos acá dejaba dos títulos y un «Volver a Activa» fuera de lugar.
 * Sin la prop, la página se ve idéntica a antes.
 */
export default function NoContactarPage({ embebida = false }: { embebida?: boolean } = {}) {
  const equipo = useEsEquipoDeenex();
  const [lista, setLista] = useState<Fila[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // form de alta
  const [contacto, setContacto] = useState("");
  const [canal, setCanal] = useState<"WhatsApp" | "Email">("WhatsApp");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [levantandoId, setLevantandoId] = useState("");
  const simuladas = lista.filter((n) => n.simulado === true).length;

  const cargar = async () => {
    try {
      const r = await activaApi.dncList();
      setLista(r.data || []);
      setError("");
    } catch {
      setError("No pudimos cargar la lista.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // Levantar una baja: revierte un falso positivo (alguien que se dio de baja por error, o que
  // volvió a dar consentimiento). Deja rastro en el backend y la persona vuelve a ser contactable.
  // El motivo lo escribe el operador: con uno fijo, la regla del backend que exige decir por qué no
  // servía de nada y la auditoría decía siempre lo mismo. Vacío o cancelado = no se hace nada.
  const levantar = async (n: Fila) => {
    if (!equipo || !n.id || levantandoId) return;
    const motivo = window.prompt(`¿Por qué levantás la baja de ${n.contacto}? Vuelve a ser contactable y queda registrado.`)?.trim();
    if (!motivo) return;
    setLevantandoId(n.id);
    setError("");
    try {
      exigirOk(await activaApi.dncLevantar(n.id, motivo));
      await cargar(); // dncList filtra activo:true → la fila levantada desaparece sola
    } catch (e: any) {
      setError(e?.message || "No se pudo levantar la baja.");
    } finally {
      setLevantandoId("");
    }
  };

  const agregar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contacto.trim() || enviando) return;
    setEnviando(true);
    setError("");
    try {
      // req() devuelve el 422 sin lanzar: sin esta guarda, un contacto inválido vaciaba el input y
      // recargaba la lista como si la baja se hubiera guardado
      exigirOk(await activaApi.dncAdd(contacto.trim(), canal, motivo.trim() || "Agregado manualmente"));
      // se limpia SÓLO si salió bien: si no, el operador corrige lo tipeado en vez de reescribirlo
      setContacto(""); setMotivo("");
      await cargar();
    } catch (e: any) {
      setError(e?.message || "No se pudo agregar. Revisá el contacto.");
    } finally {
      setEnviando(false);
    }
  };

  const contenido = (
    <>
        {!embebida && (
          <>
            <Link to="/activa" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors">
              <ArrowLeft size={14} /> Volver a Activa
            </Link>

            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0"><Ban size={20} /></div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Lista de no-contactar</h1>
                <p className="text-sm text-slate-500">Se respeta siempre, antes de cualquier envío. Nadie de esta lista vuelve a recibir campañas.</p>
              </div>
            </div>
          </>
        )}

        {/* Alta manual */}
        <form onSubmit={agregar} className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Agregar a la lista</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={contacto}
              onChange={(e) => setContacto(e.target.value)}
              placeholder="Teléfono (+549…) o email"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
            />
            <select
              value={canal}
              onChange={(e) => setCanal(e.target.value as "WhatsApp" | "Email")}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option value="WhatsApp">WhatsApp</option>
              <option value="Email">Email</option>
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Motivo (opcional)"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
            />
            <button
              type="submit"
              disabled={enviando || !contacto.trim()}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40 hover:bg-slate-800 transition-colors"
            >
              {enviando ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Agregar
            </button>
          </div>
        </form>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {lista.length} {plural(lista.length, "persona", "personas")}{simuladas > 0 ? ` · ${simuladas} del modo simulado` : ""}
            </p>
            <p className="text-[11px] text-slate-400">Una baja por un canal excluye a la persona en todos</p>
          </div>
          {/* Levantar una baja es del equipo (29/10/2026): una persona de un cliente ve a quién pedírselo, una vez. */}
          {!equipo && !loading && lista.length > 0 && (
            <p className="px-4 py-2 border-b border-slate-50 text-[11px] text-slate-500">{TEXTO_LEVANTAR_SOLO_EQUIPO}</p>
          )}

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-slate-400 text-sm"><Loader2 size={16} className="animate-spin" /> Cargando…</div>
          ) : lista.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">Nadie en la lista todavía.</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {lista.map((n, i) => (
                <div key={n.id || i} className="flex items-center gap-3 px-4 py-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${n.canal === "WhatsApp" ? "bg-emerald-50 text-emerald-600" : "bg-violet-50 text-violet-600"}`}>
                    {n.canal === "WhatsApp" ? <MessageCircle size={15} /> : <Mail size={15} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono text-slate-700 truncate">{contactoParaMostrar(n)}</p>
                    {/* en el celular la fecha va acá abajo: a la derecha le comía el lugar al número, que se cortaba en «+54911…» */}
                    <p className="text-[11px] text-slate-400">{n.motivo || "—"}<span className="sm:hidden">{n.createdAt ? ` · ${fmtFecha(n.createdAt)}` : ""}</span></p>
                    {n.simulado === true && <p className="text-[11px] font-semibold text-amber-700">{TEXTO_SIMULADA}</p>}
                  </div>
                  <span className="hidden sm:inline text-[11px] text-slate-400 flex-shrink-0">{fmtFecha(n.createdAt)}</span>
                  {equipo && (
                    <button
                      onClick={() => levantar(n)}
                      disabled={!n.id || levantandoId === n.id}
                      title="Levantar la baja: la persona vuelve a ser contactable"
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-600 disabled:opacity-40 transition-colors flex-shrink-0"
                    >
                      {levantandoId === n.id ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />} Levantar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400">
          Quien responde "BAJA" por WhatsApp o se da de baja desde el link del mail entra solo a esta lista.
          Las bajas que lleguen por otro medio se cargan a mano con el formulario.
        </p>
    </>
  );

  if (embebida) return <div className="space-y-6">{contenido}</div>;

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-3xl space-y-6">{contenido}</div>
    </div>
  );
}
